import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

/**
 * Cellsius — « Panier : Offre supérieure » (port du script Deployer FR, sémantique « compléter »).
 *
 * Quand la quantité d'un produit principal franchit un seuil dans le tiroir (ex. Kniekissen 1 → 2), on s'assure
 * que le panier contient AU MOINS la quantité cible du produit additionnel (ex. 2 Ersatzbezüge) : on complète la
 * différence, jamais plus. 3+ déjà présents → on ne touche à rien. Même règle pour le produit complémentaire.
 * Config : JSON #SuperiorOfferCartConfig (snippet superior-offer-cart-config ← metaobject panier_offre_superieure).
 * Le snapshot « avant » est capturé de façon synchrone au début du handler, avant tout await.
 */

const SOURCE = 'superior-offer-bundle';
const originalFetch = window.fetch.bind(window);

const getState = () => {
  if (!window.__superiorOfferCartState) {
    window.__superiorOfferCartState = { isSyncing: false, previousCartItems: null, ready: null };
  }
  return window.__superiorOfferCartState;
};

const getCartSections = () => {
  const sections = ['auto-gifts'];
  const id = document.querySelector('cart-drawer')?.dataset.sectionId;
  if (id) sections.push(id);
  return sections.join(',');
};

const getCart = async () => {
  const response = await originalFetch('/cart.js', { headers: { Accept: 'application/json' } });
  return response.ok ? response.json() : null;
};

const toInt = (raw, fallback = 0) => {
  if (raw == null || raw === '') return fallback;
  if (typeof raw === 'number') return Number.isFinite(raw) ? Math.trunc(raw) : fallback;
  if (typeof raw === 'object' && raw.value != null) return toInt(raw.value, fallback);
  const n = Number.parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) ? n : fallback;
};

const toId = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const m = raw.match(/\/(\d+)\s*$/);
    if (m) return Number(m[1]);
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * Quantité d'un produit au panier. `paidOnly` ignore les lignes à 0 € (produits offerts par une réduction automatique,
 * ex. les 2 Kniekissen gratuits de l'offre Nackenkissen) : seul un achat réel du produit principal déclenche l'offre.
 */
const sumQty = (items, productId, { paidOnly = false } = {}) => {
  if (!items?.length || productId == null) return 0;
  const pid = Number(productId);
  return items.reduce((sum, item) => {
    if (Number(item.product_id) !== pid) return sum;
    if (paidOnly && (parseInt(item.final_line_price, 10) || 0) === 0) return sum;
    return sum + (parseInt(item.quantity, 10) || 0);
  }, 0);
};

const findLine = (items, productId, variantId) => {
  if (!items?.length) return null;
  const pid = productId != null ? Number(productId) : null;
  const vid = variantId != null ? Number(variantId) : null;
  return (
    items.find((item) => vid != null && Number(item.variant_id) === vid) ||
    items.find((item) => pid != null && Number(item.product_id) === pid) ||
    null
  );
};

/** Config lue en priorité dans le HTML de section reçu avec l'événement (toujours à jour), sinon dans le DOM. */
const readConfigMap = (event) => {
  let text = null;
  const sectionId = document.querySelector('cart-drawer')?.dataset.sectionId;
  const html = sectionId ? event?.detail?.data?.sections?.[sectionId] : null;
  if (html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    text = parsed.getElementById('SuperiorOfferCartConfig')?.textContent;
  }
  if (!text?.trim()) text = document.getElementById('SuperiorOfferCartConfig')?.textContent;
  if (!text?.trim()) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const listOffers = (config) => {
  if (!config) return [];
  if (Array.isArray(config.offers)) return config.offers.filter(Boolean);
  if (Array.isArray(config.offer)) return config.offer.filter(Boolean);
  if (config.offer && typeof config.offer === 'object') return [config.offer];
  return [];
};

const normalizeOffer = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.threshold == null && raw.additional_qty_to_add == null && raw.complementary_qty_to_add == null) return null;
  return {
    threshold: toInt(raw.threshold),
    additionalProductId: toId(raw.additional_product_id),
    additionalVariantId: toId(raw.additional_variant_id),
    additionalTarget: toInt(raw.additional_qty_to_add),
    complementaryProductId: toId(raw.complementary_product_id),
    complementaryVariantId: toId(raw.complementary_variant_id),
    complementaryTarget: toInt(raw.complementary_qty_to_add),
  };
};

/** L'offre au seuil le plus élevé atteint par la quantité (ex. seuils 2 et 4 → 3 Kissen = offre « 2 »). */
const pickOffer = (rawOffers, mainQty) => {
  const offers = rawOffers.map(normalizeOffer).filter((o) => o && o.threshold >= 1 && mainQty >= o.threshold);
  if (offers.length === 0) return null;
  return offers.sort((a, b) => b.threshold - a.threshold)[0];
};

const parseCartJson = async (response) => {
  if (!response.ok) return null;
  const data = await response.json();
  if (data?.errors) {
    console.warn('[superior-offer] cart API errors', data.errors);
    return null;
  }
  return data;
};

const changeLineQty = (lineKey, quantity) =>
  originalFetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity, sections: getCartSections(), sections_url: window.location.pathname }),
  }).then(parseCartJson);

const addVariants = (items) =>
  originalFetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: getCartSections(), sections_url: window.location.pathname }),
  }).then(parseCartJson);

/** Complète le produit jusqu'à `target` unités (jamais de retrait). */
const topUp = async (cart, { productId, variantId, target, label }) => {
  if (!target || target < 1 || !variantId) return { cart, cartData: null };
  const current = sumQty(cart.items, productId);
  const missing = target - current;
  if (missing <= 0) return { cart, cartData: null };

  const line = findLine(cart.items, productId, variantId);
  const cartData = line
    ? await changeLineQty(line.key, (parseInt(line.quantity, 10) || 0) + missing)
    : await addVariants([{ id: variantId, quantity: missing }]);
  console.info('[superior-offer] top-up', label, { current, target, added: missing });
  const nextCart = cartData ? await getCart() : cart;
  return { cart: nextCart || cart, cartData };
};

const applyOffer = async (cart, offer) => {
  let lastCartData = null;
  let nextCart = cart;
  const steps = [
    { label: 'additional', productId: offer.additionalProductId, variantId: offer.additionalVariantId, target: offer.additionalTarget },
    { label: 'complementary', productId: offer.complementaryProductId, variantId: offer.complementaryVariantId, target: offer.complementaryTarget },
  ];
  for (const step of steps) {
    try {
      const result = await topUp(nextCart, step);
      nextCart = result.cart;
      if (result.cartData) lastCartData = result.cartData;
    } catch (error) {
      console.error('[superior-offer] step failed', step.label, error);
    }
  }
  return lastCartData;
};

const didCrossThreshold = (oldQty, newQty, threshold) => oldQty < threshold && newQty >= threshold && newQty > oldQty;

const syncPreviousCartItems = async () => {
  const cart = await getCart();
  getState().previousCartItems = cart?.items ?? [];
};

/** Détecte les franchissements de seuil entre deux états du panier et complète les produits inclus. */
const evaluateAndApply = async (newCart, previousItems, event) => {
  const state = getState();
  const configMap = readConfigMap(event);
  const toApply = [];
  for (const [mainProductIdStr, rawConfig] of Object.entries(configMap)) {
    const mainProductId = Number(mainProductIdStr);
    if (!Number.isFinite(mainProductId)) continue;
    const oldQty = sumQty(previousItems, mainProductId, { paidOnly: true });
    const newQty = sumQty(newCart.items, mainProductId, { paidOnly: true });
    const rawOffers = listOffers(rawConfig);
    const crossed = rawOffers.map(normalizeOffer).some((o) => o?.threshold && didCrossThreshold(oldQty, newQty, o.threshold));
    if (!crossed) continue;
    const picked = pickOffer(rawOffers, newQty);
    if (picked) toApply.push(picked);
  }
  if (toApply.length === 0) return false;

  state.isSyncing = true;
  let lastCartData = null;
  let cart = newCart;
  try {
    for (const offer of toApply) {
      const cartData = await applyOffer(cart, offer);
      if (cartData) {
        lastCartData = cartData;
        cart = (await getCart()) || cart;
      }
    }
    if (lastCartData) {
      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount: lastCartData.item_count, source: SOURCE, sections: lastCartData.sections }),
      );
    }
  } finally {
    state.isSyncing = false;
  }
  return Boolean(lastCartData);
};

const onCartUpdated = (event) => {
  const state = getState();
  if (state.isSyncing) return;
  const source = event.detail?.data?.source;
  if (source === SOURCE) return;
  const previousItemsSnapshot = state.previousCartItems ?? [];

  void (async () => {
    try {
      if (state.ready) await state.ready;
      if (state.isSyncing) return;
      const newCart = await getCart();
      if (!newCart?.items) {
        await syncPreviousCartItems();
        return;
      }
      state.previousCartItems = newCart.items;
      await evaluateAndApply(newCart, previousItemsSnapshot, event);
      await syncPreviousCartItems();
    } catch (error) {
      console.error('[superior-offer] sync failed', error);
      await syncPreviousCartItems();
    } finally {
      getState().isSyncing = false;
    }
  })();
};

const state = getState();
if (!state.ready) state.ready = syncPreviousCartItems();
if (window.__superiorOfferCartHandler) document.removeEventListener(ThemeEvents.cartUpdated, window.__superiorOfferCartHandler);
window.__superiorOfferCartHandler = onCartUpdated;
document.addEventListener(ThemeEvents.cartUpdated, onCartUpdated);
